import { AlertTriangle, Boxes, Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Progress } from '@/components/ui/progress';
import { formatDateByDayjs } from '@/utils';
import { SectionHeader } from './SectionHeader';
import {
  NOT_RETURNED_ROW,
  RETURN_LINE,
  boxReturnState,
  isReturnLeg,
  returnTally,
} from './boxReturnState';

export interface OrderBoxDetail {
  sequence: number;
  size: string;
  received_at?: string | null;
  delivered_at?: string | null;
  rto_received_at?: string | null;
}

interface PackagesSectionProps {
  boxes: OrderBoxDetail[];
  statusSlug?: string;
  labelsStale?: boolean;
  onPrintLabels?: () => void;
}

/** Statuses where the order is still in our building, waiting for its boxes. */
const IN_WAREHOUSE = ['collected', 'received-warehouse', 'sorted'];

/**
 * BOXY-301 §5.8 — one row per physical box, with its size.
 *
 * BOXY-618 — the warehouse SCAN status ("not scanned in yet" / the scan time)
 * is internal ops information: it was asked for the admin screens, not for
 * merchants, and it confused them. The RETURN state stays, because "did my
 * box come back" is the merchant's own question (BOXY-556).
 */
export function PackagesSection({
  boxes,
  statusSlug,
  labelsStale,
  onPrintLabels,
}: PackagesSectionProps) {
  const { t } = useTranslation();
  const count = boxes.length;
  const received = boxes.filter((box) => box.received_at).length;
  const held = received > 0 && received < count && IN_WAREHOUSE.includes(statusSlug ?? '');
  // BOXY-556 — on the return leg the header counts boxes back, not boxes in.
  const returnLeg = isReturnLeg(statusSlug);
  const tally = returnTally(boxes);
  const progress = tally.count ? (tally.returned / tally.count) * 100 : 100;

  /** The return line, or null on the forward leg — where nothing is shown. */
  const stateOf = (box: OrderBoxDetail) => {
    const returnState = boxReturnState(box, statusSlug);
    if (!returnState) return null;
    const line = RETURN_LINE[returnState];
    return {
      Icon: line.Icon,
      label: t(line.labelKey),
      at: line.at ? (box[line.at] ?? null) : null,
      tone: line.tone,
      returnState,
    };
  };

  return (
    <div
      className='rounded-2xl border border-sd-border bg-sd-card p-5'
      data-testid='packages-section'
    >
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <SectionHeader icon={Boxes} title={String(t('packages_count_title', { count }))} />
        {returnLeg ? (
          <div className='flex min-w-[10rem] items-center gap-2 text-xs text-sd-muted-foreground'>
            <span className='whitespace-nowrap'>
              {String(t('boxes_returned_of', { received: tally.returned, count: tally.count }))}
            </span>
            <Progress value={progress} className='h-1.5' />
          </div>
        ) : null}
      </div>

      {labelsStale ? (
        <div className='mt-3 flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800'>
          <span className='flex items-center gap-2'>
            <AlertTriangle className='h-4 w-4 shrink-0' />
            {String(t('labels_stale_reprint_all'))}
          </span>
        </div>
      ) : null}

      <ul className='mb-0 mt-3 list-none divide-y divide-sd-input rounded-lg ps-0 ring-1 ring-inset ring-sd-input'>
        {boxes.map((box) => {
          const state = stateOf(box);
          const Icon = state?.Icon;
          return (
            <li
              key={box.sequence}
              data-state={state?.returnState}
              className={`flex items-center gap-3 px-3 py-2.5 text-sm ${state?.returnState === 'not_returned' ? NOT_RETURNED_ROW : ''}`}
            >
              <span className='w-16 shrink-0 font-medium tabular-nums'>{`BOX ${box.sequence}/${count}`}</span>
              <span className='w-8 shrink-0 rounded border border-sd-border bg-sd-muted text-center text-xs font-medium'>
                {box.size}
              </span>
              {state && Icon ? (
                <>
                  <span className={`flex min-w-0 flex-1 items-center gap-1.5 ${state.tone}`}>
                    <Icon className='h-4 w-4 shrink-0' />
                    <span className='truncate'>{String(state.label)}</span>
                  </span>
                  <span
                    className='shrink-0 text-xs tabular-nums text-sd-muted-foreground'
                    dir='ltr'
                  >
                    {state.at ? formatDateByDayjs(state.at, 'DD MMM, HH:mm') : '—'}
                  </span>
                </>
              ) : (
                <span className='flex-1' />
              )}
            </li>
          );
        })}
      </ul>

      {held ? (
        <p className='mb-0 mt-3 flex items-center gap-2 text-xs text-sd-muted-foreground'>
          <AlertTriangle className='h-3.5 w-3.5 shrink-0 text-amber-600' />
          {String(t('shipment_held_until_all_boxes'))}
        </p>
      ) : null}

      {onPrintLabels ? (
        <button
          type='button'
          onClick={onPrintLabels}
          className='mt-3 inline-flex h-9 items-center gap-2 rounded-md bg-sd-card px-3 ring-1 ring-inset ring-sd-input text-sm font-medium hover:bg-sd-muted'
        >
          <Download className='h-4 w-4' />
          {String(t('print_all_labels'))}
        </button>
      ) : null}
    </div>
  );
}
